import { useId, useState, type SyntheticEvent } from "react";

import { BOT_DIFFICULTIES, type BotDifficulty } from "../../bot/difficulty";
import { type Player } from "../../game";
import { type MatchMode } from "../match/matchMode";

import "./MatchSetup.css";

export type MatchSetupSelection = Readonly<{
  mode?: MatchMode;
  humanPlayer: Player;
  difficulty: BotDifficulty;
}>;

export type MatchSetupProps = Readonly<{
  /**
   * The setup to present for a fresh match or rematch. Mounting a new setup
   * surface with the prior choice lets a player keep or change it.
   */
  initialSelection?: MatchSetupSelection;
  discMarkers?: boolean;
  title?: string;
  description?: string;
  submitLabel?: string;
  cancelLabel?: string;
  disabled?: boolean;
  onCancel?: () => void;
  onStart: (selection: MatchSetupSelection) => void;
}>;

const DEFAULT_SELECTION: MatchSetupSelection = Object.freeze({
  mode: "bot",
  humanPlayer: "black",
  difficulty: "medium",
});

const PLAYER_LABELS: Readonly<Record<Player, string>> = Object.freeze({
  black: "Black",
  red: "Red",
});

const DIFFICULTY_LABELS: Readonly<Record<BotDifficulty, string>> = Object.freeze({
  easy: "Easy",
  medium: "Medium",
  hard: "Hard",
  extreme: "Extreme",
});

function selectionOrDefault(
  initialSelection: MatchSetupSelection | undefined,
): MatchSetupSelection {
  return initialSelection === undefined
    ? DEFAULT_SELECTION
    : Object.freeze({ ...initialSelection, mode: initialSelection.mode ?? "bot" });
}

/**
 * Complete match configuration for bot and local hot-seat play.
 *
 * This component deliberately owns only draft form state. It does not create a
 * match, persist choices, or decide which controller should take a turn.
 */
export function MatchSetup({
  initialSelection,
  discMarkers = false,
  title = "New game",
  description,
  submitLabel = "Play",
  cancelLabel = "Cancel",
  disabled = false,
  onCancel,
  onStart,
}: MatchSetupProps) {
  const [selection, setSelection] = useState<MatchSetupSelection>(() =>
    selectionOrDefault(initialSelection),
  );
  const titleId = useId();
  const modeLegendId = useId();
  const colorLegendId = useId();
  const optionId = useId();

  const handleSubmit = (event: SyntheticEvent<HTMLFormElement>): void => {
    event.preventDefault();
    onStart(selection);
  };

  return (
    <section className="match-setup" aria-labelledby={titleId}>
      <div className="match-setup__heading">
        <h2 id={titleId}>{title}</h2>
        {description === undefined ? null : <p>{description}</p>}
      </div>

      <form className="match-setup__form" onSubmit={handleSubmit}>
        <fieldset className="match-setup__fieldset" aria-describedby={modeLegendId}>
          <legend>Players</legend>
          <span className="match-setup__sr-only" id={modeLegendId}>
            Choose an on-device bot or two players sharing this device.
          </span>
          <div className="match-setup__choices match-setup__choices--modes">
            {(["bot", "local"] as const).map((mode) => {
              const inputId = `${optionId}-mode-${mode}`;
              return (
                <label key={mode} className="match-setup__choice-label" htmlFor={inputId}>
                  <input
                    id={inputId}
                    className="match-setup__radio"
                    type="radio"
                    name="match-mode"
                    value={mode}
                    disabled={disabled}
                    checked={selection.mode === mode}
                    onChange={() => setSelection((current) => ({ ...current, mode }))}
                  />
                  <span className="match-setup__choice match-setup__choice--plain">
                    <strong>{mode === "bot" ? "Bot" : "Two players"}</strong>
                  </span>
                </label>
              );
            })}
          </div>
        </fieldset>

        {selection.mode === "bot" ? (
          <>
            <fieldset className="match-setup__fieldset" aria-describedby={colorLegendId}>
              <legend>Your color</legend>
              <span id={colorLegendId} className="match-setup__sr-only">
                Black always starts.
              </span>
              <div className="match-setup__choices match-setup__choices--colors">
                {(Object.keys(PLAYER_LABELS) as Player[]).map((player) => {
                  const inputId = `${optionId}-player-${player}`;

                  return (
                    <label key={player} className="match-setup__choice-label" htmlFor={inputId}>
                      <input
                        id={inputId}
                        className="match-setup__radio"
                        type="radio"
                        name="human-player"
                        value={player}
                        disabled={disabled}
                        checked={selection.humanPlayer === player}
                        onChange={() =>
                          setSelection((current) => ({ ...current, humanPlayer: player }))
                        }
                      />
                      <span className="match-setup__choice match-setup__choice--color">
                        <span
                          className={`match-setup__disc-cue match-setup__disc-cue--${player}${discMarkers ? " match-setup__disc-cue--marked" : ""}`}
                          aria-hidden="true"
                        />
                        <strong>{PLAYER_LABELS[player]}</strong>
                      </span>
                    </label>
                  );
                })}
              </div>
            </fieldset>

            <fieldset className="match-setup__fieldset">
              <legend>Difficulty</legend>
              <div className="match-setup__choices match-setup__choices--difficulty">
                {BOT_DIFFICULTIES.map((difficulty) => {
                  const inputId = `${optionId}-difficulty-${difficulty}`;

                  return (
                    <label key={difficulty} className="match-setup__choice-label" htmlFor={inputId}>
                      <input
                        id={inputId}
                        className="match-setup__radio"
                        type="radio"
                        name="bot-difficulty"
                        value={difficulty}
                        disabled={disabled}
                        checked={selection.difficulty === difficulty}
                        onChange={() => setSelection((current) => ({ ...current, difficulty }))}
                      />
                      <span className="match-setup__choice match-setup__choice--plain">
                        <strong>{DIFFICULTY_LABELS[difficulty]}</strong>
                      </span>
                    </label>
                  );
                })}
              </div>
            </fieldset>
          </>
        ) : null}

        <div className="match-setup__actions">
          {onCancel === undefined ? null : (
            <button
              className="match-setup__cancel"
              disabled={disabled}
              type="button"
              onClick={onCancel}
            >
              {cancelLabel}
            </button>
          )}
          <button className="match-setup__start" disabled={disabled} type="submit">
            {submitLabel}
          </button>
        </div>
      </form>
    </section>
  );
}
